import React from "react";
import {
    TextField,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography
} from "@mui/material";

import TicketAutocompleteField
    from "./TicketAutocompleteField";
import UserSearchAutocomplete from "./UserAutocompleteField";

const DynamicAssetField = ({
    field,
    value,
    onChange,
    users = [],
    departments = [],
    assignmentTypes = [],
    disposalMethods = [],
    warehouseNames = [],
    vendors = [],
    lostTypes = [],
    selectedTicket = [],
    selectedUser=[],
    maintenanceTypes=[]
}) => {

    /*
     * All dropdown data sources.
     *
     * field.optionsKey determines which
     * array should be used.
     */
    console.log("Departments:", departments);
    const optionSources = {
        users,
        departments,
        assignmentTypes,
        disposalMethods,
        warehouseNames,
        vendors,
        lostTypes,
        selectedTicket,
        selectedUser,
        maintenanceTypes
    };

    const options =
        optionSources[field.optionsKey] || [];


    /*
     * AUTOCOMPLETE
     */
    if (field.type === "autocomplete" &&
        field.optionsKey === "users") {

        const selectedOption =
            options.find(
                option =>
                    String(option.id) === String(value)
            ) || null;

        return (
            <Autocomplete
                options={options}
                value={selectedOption}
                fullWidth

                getOptionLabel={(option) =>
                    option?.name || ""
                }

                isOptionEqualToValue={(
                    option,
                    selected
                ) =>
                    String(option.id) ===
                    String(selected.id)
                }

                onChange={(event, newValue) => {

                    onChange({
                        target: {
                            name: field.name,
                            value:
                                newValue?.id || ""
                        }
                    });

                }}

                sx={{
                    width: "100%",
                    minWidth: 0
                }}

                slotProps={{
                    root: {
                        sx: {
                            width: "100%",
                            minWidth: 0
                        }
                    }
                }}

                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        label={field.label}
                        required={field.required}
                    />
                )}
            />
        );
    }

    /*
    * Autocomplete for ticket
    */
    if (field.type === "ticketAutocomplete") {

        return (
            <TicketAutocompleteField
                field={field}
                value={value}
                onChange={onChange}
                selectedTicket={selectedTicket}
            />
        );
    }

    if (field.type === "userAutocomplete") {

        return (
            <UserSearchAutocomplete
                field={field}
                value={value}
                onChange={onChange}
                selectedUser={selectedUser}

            />
        );
    }

    /*
     * SELECT / DROPDOWN
     */
    if (field.type === "select") {

        return (
            <FormControl
                fullWidth
                size="small"
                sx={{
                    minWidth: 0
                }}
            >

                <InputLabel>
                    {field.label}
                </InputLabel>

                <Select
                    value={value ?? ""}
                    label={field.label}

                    onChange={(event) => {

                        onChange({
                            target: {
                                name: field.name,
                                value:
                                    event.target.value
                            }
                        });

                    }}
                >

                    {options.map((option) => (

                        <MenuItem
                            key={option.id}
                            value={option.id}
                        >
                            {option.name}
                        </MenuItem>

                    ))}

                </Select>

            </FormControl>
        );
    }




    if (
        field.type === "autocomplete" &&
        field.optionsKey === "warehouseNames"
    ) {
        const selectedWarehouse =
            warehouseNames.find(
                warehouse =>
                    Number(warehouse.id) === Number(value)
            ) || null;

        return (
            <Box sx={{ width: "100%", minWidth: 0 }}>

                <Autocomplete
                    options={warehouseNames}
                    value={selectedWarehouse}
                    fullWidth
                    isOptionEqualToValue={(option, selected) =>
                        Number(option.id) === Number(selected.id)
                    }
                    getOptionLabel={(option) =>
                        option?.name || ""
                    }
                    onChange={(event, newValue) => {

                        onChange({
                            target: {
                                name: field.name,
                                value: newValue?.id ?? ""
                            }
                        });

                    }}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>

                            <Box>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                >
                                    {option.name}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {option.code}
                                    {option.city
                                        ? ` • ${option.city}`
                                        : ""}
                                </Typography>
                            </Box>

                        </li>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            label={field.label}
                            required={field.required}
                        />
                    )}
                />

                {selectedWarehouse && (
                    <Box
                        sx={{
                            mt: 1,
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            backgroundColor: "background.paper"
                        }}
                    >

                        <Typography
                            variant="body2"
                            fontWeight={600}
                        >
                            {selectedWarehouse.name}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {selectedWarehouse.addressLine1}
                        </Typography>

                        {selectedWarehouse.addressLine2 && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {selectedWarehouse.addressLine2}
                            </Typography>
                        )}

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {[
                                selectedWarehouse.city,
                                selectedWarehouse.state,
                                selectedWarehouse.postalCode
                            ]
                                .filter(Boolean)
                                .join(", ")}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {selectedWarehouse.country}
                        </Typography>

                    </Box>
                )}

            </Box>
        );
    }

    /*
    * TEXT / DATE / TEXTAREA
    */
    return (
        <TextField
            fullWidth
            name={field.name}
            label={field.label}
            value={value ?? ""}
            onChange={onChange}
            required={field.required}

            type={
                field.type === "date"
                    ? "date"
                    : "text"
            }

            multiline={
                field.type === "textarea"
            }

            rows={
                field.type === "textarea"
                    ? 3
                    : undefined
            }

            slotProps={
                field.type === "date"
                    ? {
                        inputLabel: {
                            shrink: true
                        }
                    }
                    : undefined
            }
        />
    );
};

export default DynamicAssetField;